import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root',
})
export class DDIService {
  private className = 'DDIService';

  private implementsConfigRaw: any = null;
  private isobusManufRaw: any = null;
  private ddisConfig: any = null;
  private possibleDDIs: string[][] = [];
  private allDDIs: string[] = [];
  private ddiUnitVoncersionConfig: any = null;

  constructor(
    private httpClient: HttpClient,
    private errorHandler: ErrorHandlerService,
    private log: NGXLogger
  ) {
    this.getISOBUSManufacturerCodes();
    this.getDDIsConf();
    this.getDDIUnitConversionConf();

    this.log.debug(this.className + '- Construct');
  }
  private getISOBUSManufacturerCodes() {
    if (!this.isobusManufRaw) {
      return this.httpClient.get('../../assets/ressources/isobus_manufacturer_codes.json').subscribe((data) => {
        '../';
        this.isobusManufRaw = data;
      });
    }
    return of(this.isobusManufRaw);
  }

  private getDDIsConf() {
    if (!this.ddisConfig) {
      return this.httpClient.get('../../assets/ressources/ddi_config.json').subscribe((data) => {
        this.ddisConfig = data['ddis'];
        this.allDDIs = [];
        for (const ddi in this.ddisConfig) {
          this.allDDIs.push(ddi);
        }
        // preload DDIs for implementType
        const implType1s = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26];
        for (const type1 of implType1s) {
          this.loadPossibleDDIsForType(type1);
        }
      });
    }
    return of(this.ddisConfig);
  }

  private getDDIUnitConversionConf() {
    if (!this.ddiUnitVoncersionConfig) {
      return this.httpClient.get('../../assets/ressources/ddi_conversion_config.json').subscribe((data) => {
        this.ddiUnitVoncersionConfig = data['unit_conversion'];
      });
    }
    return of(this.ddiUnitVoncersionConfig);
  }

  getISOBUSManufacturer(manufacturerCode: string): string {
    if (this.isobusManufRaw.hasOwnProperty(manufacturerCode)) {
      return this.isobusManufRaw[manufacturerCode];
    } else {
      return null;
    }
  }

  private loadPossibleDDIsForType(type1: number) {
    const isobusClasses = this.convertExaImplemenTypeToISOBUSImplementType(type1);
    let ddiListForType = [];
    for (const key in this.ddisConfig) {
      const typicalDeviceClasses = this.ddisConfig[key]['typicalDeviceClasses'];
      // check if one of isobusClasses is in typicalDeviceClasses
      for (const isoclass of isobusClasses) {
        if (typicalDeviceClasses.indexOf(isoclass) > -1) {
          ddiListForType.push(key);
          break; // stop here and check next ddi(key)
        }
      }
    }
    this.possibleDDIs[type1] = ddiListForType;
  }

  getPossibleDDIsForImplement(type1: number): string[] {
    return this.possibleDDIs[type1];
  }

  getAllDDIs(): string[] {
    return this.allDDIs;
  }

  private convertExaImplemenTypeToISOBUSImplementType(type1: number): number[] {
    let defaultList = [17]; //[13, 17]; // Powered Auxiliary Devices / Sensor System
    let specialList = [];
    switch (type1) {
      case 1:
        specialList = [9];
        break; // Forage
      case 2:
        specialList = [2, 3];
        break; // Tillage / Secondary Tillage
      case 3:
        specialList = [4];
        break; // Seeder/Planter
      case 4:
        specialList = [5];
        break; // Fertilizer
      case 5:
        specialList = [6];
        break; // Sprayer
      case 6:
        specialList = [25];
        break; // Slurry/Manure
      case 7:
        specialList = [6, 10];
        break; // Sprayer / Irrigation
      case 8:
        specialList = [3, 4, 8];
        break; // Secondary Tillage / Seeder/Planter / Root Harvester
      case 9:
        specialList = [9, 23];
        break; // Forage / Mulcher
      case 10:
        specialList = [11];
        break; // Transport
      case 11:
        specialList = [26];
        break; // Feeder/Mixer
      case 12:
        specialList = [19, 20, 21, 22, 23];
        break; // Timber Harvesters / Forwarders / Timber Loaders / Timber Processing Machines / Mulcher
      case 13:
        specialList = [15, 23, 24];
        break; // Earth Work / Mulcher / UtilityVehicle
      case 14:
        specialList = [12, 15];
        break; // Farm Yard Operations / Earth Work
      case 15:
        specialList = [7];
        break; // Harvesters
    }

    return defaultList.concat(specialList);
  }

  getBaseUnitOfDDI(ddi: string): string {
    ddi = ddi.split('_')[0];
    if (this.ddiUnitVoncersionConfig[ddi]) {
      return this.ddiUnitVoncersionConfig[ddi]['baseUnit'];
    }
    return null;
  }

  getUnitOfDDI(ddi: string): string {
    ddi = ddi.split('_')[0];
    if (this.ddiUnitVoncersionConfig[ddi]) {
      return this.ddiUnitVoncersionConfig[ddi]['convUnit'];
    }
    return null;
  }

  convertDDIValue(ddi: string, value: number): number {
    ddi = ddi.split('_')[0];
    if (this.ddiUnitVoncersionConfig[ddi]) {
      const offset = this.ddiUnitVoncersionConfig[ddi]['offset'];
      const factor = this.ddiUnitVoncersionConfig[ddi]['factor'];
      return value * factor + offset;
    }
    return value;
  }

  convertDDIValueBack(ddi: string, value: number): number {
    ddi = ddi.split('_')[0];
    if (this.ddiUnitVoncersionConfig[ddi]) {
      const offset = this.ddiUnitVoncersionConfig[ddi]['offset'];
      const factor = this.ddiUnitVoncersionConfig[ddi]['factor'];
      return (value - offset) / factor;
    }
    return value;
  }
}
